import { Box, useIsMounted, useTheme } from '@simplywallst/ui-core';
import { ChangeEvent, ReactNode, useRef } from 'react';
import { SliderLabels } from './components/SliderLabels';
import { SliderTextInput } from './components/SliderTextInput';
import { StyledSlider } from './styled';
import {
  SliderTrackBandInput,
  sliderNormaliseBands,
  sliderTrackBandColor,
  sliderValuePercentage,
} from './utils';
import { parseNumberSafe } from '../../../Narrative/utils';

type Props = {
  identifier: string;
  value: number;
  precision: number;
  minRange: number;
  maxRange: number;
  bands?: SliderTrackBandInput[];
  suffix?: ReactNode;
  onChange: (value: number) => void;
};

const THUMB_SIZE = 20;

export const Slider = ({
  value,
  precision,
  onChange,
  minRange,
  maxRange,
  identifier,
  suffix,
  bands,
}: Props) => {
  const currentMarkerRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const isMounted = useIsMounted();
  const theme = useTheme();

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(parseNumberSafe(e.target.value, precision));
  };

  const normalisedBands = sliderNormaliseBands(
    bands,
    minRange,
    maxRange,
    theme.color.darkGray
  );
  const sliderPercentage = sliderValuePercentage(value, minRange, maxRange);

  return (
    <Box paddingTop="x5" ref={containerRef}>
      <SliderTextInput
        name={identifier}
        value={value}
        precision={precision}
        suffix={suffix}
        onChange={onChange}
        isValueValid={(newValue) =>
          newValue <= maxRange && newValue >= minRange
        }
        anchorEl={isMounted ? currentMarkerRef.current : null}
        boundaryEl={isMounted ? containerRef.current : null}
      />

      <Box position="relative">
        <StyledSlider
          type="range"
          id={identifier}
          name={identifier}
          min={minRange}
          max={maxRange}
          step="1"
          value={value}
          onChange={handleSliderChange}
          thumbSize={THUMB_SIZE}
          trackColor={sliderTrackBandColor(normalisedBands)}
        />
        <Box
          ref={currentMarkerRef}
          position="absolute"
          width="1px"
          height="1px"
          style={{
            // Due to frequency of change using style instead of prop to avoid SC overload
            left: `${sliderPercentage}%`,
          }}
        />
      </Box>

      <SliderLabels bands={normalisedBands} />
    </Box>
  );
};
